import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import { AddCircleIcon, EyeIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { FormTypes } from 'constants/forms';
import { componentMap, getComponentProps } from 'helpers/question-config';
import { useKeyboardHints } from 'hooks/keyboard-hints';

import { CollapsibleGroup } from 'components/CollapsibleBlock/CollapsibleBlock';
import FormWrapper from 'components/Form/Form/FormWrapper';
import { Button } from 'components/FormComponents';

import { QuestionRow } from './QuestionRow';

/**
 * Tab view for editing form questions (list + preview).
 * Keeps question editing separate from the questionnaire builder.
 */
const QuestionsTab = ({
  formRef,
  handleSubmit,
  handleSave,
  onError,
  fields,
  errors,
  getFieldErrorMessage,
  question,
  questionIndex,
  questionRefs,
  setQuestionPreview,
  handleAddField,
  handleFieldChange,
  handleDeleteField,
  duplicateField,
  copyField,
  pasteFieldBelow,
  pasteFieldAbove,
  copyFieldDetails,
  getLogicJumpsCount,
  getRulesCount,
  sidebarCollapsed,
  isUsingModernForm = false,
  register,
  watch,
  control,
  setValue,
  unregister,
  onDragEnd,
  setDraggingId,
  draggingId,
  textParams = {},
  translatedFields = {},
  form,
  company,
}) => {
  const allowTranslation =
    company?.ff_translations &&
    (form?.type === FormTypes.CANDIDATE || form?.type === FormTypes.REFEREE);
  const keyboardHint = useKeyboardHints(question?.type);
  const getQuestionProps = value => getComponentProps({ question: question ?? {}, value });

  const activeTranslation = question?.id ? (translatedFields[question.id] ?? null) : null;

  return (
    <>
      <form ref={formRef} onSubmit={handleSubmit(handleSave, onError)}>
        <div className="flex px-6 py-8 gap-8">
          <div
            className={cn(
              'flex flex-col gap-8',
              sidebarCollapsed
                ? 'min-[1140px]:flex-1 min-[1140px]:min-w-0 min-[1140px]:max-w-[calc(13rem+32%+3rem)]'
                : 'w-1/3',
              'max-[1140px]:flex-1 max-[1140px]:min-w-0',
            )}>
            <CollapsibleGroup newStartOpen={true}>
              <DragDropContext
                onDragStart={result => setDraggingId(result.draggableId)}
                onDragEnd={result => {
                  setDraggingId(null);
                  onDragEnd(result);
                }}>
                <Droppable droppableId="fields">
                  {({ innerRef, droppableProps, placeholder }) => (
                    <div className="flex flex-col gap-4" {...droppableProps} ref={innerRef}>
                      {fields.map((field, index) => {
                        const fieldError = errors?.fields?.[index];
                        const errorMessage = getFieldErrorMessage(fieldError);
                        return (
                          <QuestionRow
                            key={field.key}
                            index={index}
                            active={questionIndex === index}
                            formField={field}
                            logicJumpsCount={getLogicJumpsCount(field?.order)}
                            rulesCount={getRulesCount(field?.order)}
                            register={register}
                            watch={watch}
                            control={control}
                            errors={fieldError}
                            validationErrorMessage={errorMessage}
                            remove={handleDeleteField}
                            duplicate={duplicateField}
                            copyField={copyField}
                            pasteFieldBelow={pasteFieldBelow}
                            pasteFieldAbove={pasteFieldAbove}
                            unregister={unregister}
                            pasteEnabled={!!copyFieldDetails}
                            setValue={setValue}
                            onClick={setQuestionPreview}
                            onChange={handleFieldChange}
                            isDragging={draggingId === field.key}
                            textParams={textParams}
                            ref={el => {
                              if (el) {
                                questionRefs.current[index] = el;
                              } else {
                                delete questionRefs.current[index];
                              }
                            }}
                          />
                        );
                      })}
                      {placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </CollapsibleGroup>
            <Button
              className="w-1/2 u-margin-top sticky bottom-10 shadow-xl mx-auto z-10"
              type="primary"
              onClick={handleAddField}
              dataTestId="add-question-button">
              <HugeiconsIcon
                icon={AddCircleIcon}
                size="20"
                className="text-neutral-6 mr-4 text-white"
                strokeWidth="2"
              />
              Add Question
            </Button>
          </div>
          <div className="flex-1 max-[1140px]:flex-1 min-w-0"></div>
        </div>
      </form>
      {question && (
        <div className="fixed top-[calc(7.4rem+10rem+3rem)] right-6 bottom-6 left-[calc(18rem+32%+3rem)] max-[1140px]:left-[calc(7rem+46%)] z-20 cursor-not-allowed bg-yellow-2/30 p-4 rounded-3xl overflow-hidden shadow-sm border border-yellow-3/30 flex flex-col transition-[left] duration-200">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2 flex-shrink-0">
            <span className="text-xl text-orange-3 font-medium flex items-center gap-2">
              <HugeiconsIcon icon={EyeIcon} size="16" className="text-orange-3" /> Preview only -
              Not Interactive
            </span>
            <span className="text-lg text-orange-3 font-regular">
              This is a preview. Buttons and inputs do not work.
            </span>
          </div>
          <div className="flex-1 min-h-0 bg-neutral-1 border border-neutral-4 rounded-2xl shadow-sm overflow-y-auto mb-1">
            {question ? (
              <div className="h-full p-8 flex items-center justify-center pointer-events-none">
                <FormWrapper
                  hideHeader={true}
                  hideFooter={true}
                  height="70vh"
                  question={question}
                  questionIndex={
                    fields?.findIndex(
                      f => (f.id != null && f.id === question?.id) || f.key === question?.key,
                    ) ?? -1
                  }
                  allowTranslation={allowTranslation}
                  totalQuestions={fields?.length}
                  keyboardHint={keyboardHint}
                  componentMap={componentMap}
                  getComponentProps={getQuestionProps}
                  translatedText={activeTranslation?.text ?? null}
                  translatedDescription={activeTranslation?.translated_description ?? null}
                  translatedOptions={activeTranslation?.translated_options ?? null}
                  previewMode
                />
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-neutral-6 text-center p-8">
                <p className="text-xl">Select a question to preview</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default QuestionsTab;
